import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, set } from 'vue';
import { useRoute } from 'vue-router/composables';
import { useStore, useTranslate, useConfirm } from '@/composables/use-globals';
import { useGroupMembers } from '@/composables/use-group-members';
import { memberRoute } from '@/router/coach';
import { Route } from 'vue-router';
import { orderBy } from 'lodash';
import { Member } from '@/models/member';
import { Group } from '@/models/group';
import moment from 'moment';
import RegisterMember from '@/components/coach/members/RegisterMember.vue';
import MemberTagsDialog from '@/components/club/MemberTagsDialog.vue';
import GroupedMemberView from '@/components/common/GroupedMemberView.vue';
import MemberNote from '@/components/coach/members/MemberNote.vue';
import { useFeature } from '@/composables/use-feature';


export default /*#__PURE__*/_defineComponent({
  __name: 'Register',
  setup(__props) {

const translate = useTranslate();
const confirm = useConfirm();
const store = useStore();
const route = useRoute();
const { groupId, groupLink, groupDate, groupName, groupTotal, group, groupMembers } = useGroupMembers(store);
const enableAbilityGroups = useFeature(store, 'ABILITY_GROUPS');

const memberTagsDialog = ref<InstanceType<typeof MemberTagsDialog>>();
const showNote = ref<Record<string, boolean>>({});
const search = ref<string>('');
const sortBy = ref({
  name: 'sort.first_name_asc',
  field: 'firstname',
  type: 'member',
  on: true,
  direction: 'asc',
});

/**
 * sortOptions
 * Defines the available ways to sort the register list.
 *
 * @property {Array<{name: string, field: string, type: string, on: boolean, direction: string}>>}
 */
const sortOptions = [
  {
    name: 'sort.first_name_asc',
    field: 'firstname',
    type: 'member',
    on: true,
    direction: 'asc',
  },
  {
    name: 'sort.last_name_asc',
    field: 'lastname',
    type: 'member',
    on: true,
    direction: 'asc',
  },
  {
    name: 'sort.present',
    field: 'in',
    type: 'member',
    on: true,
    direction: 'desc',
  },
  {
    name: 'sort.absent',
    field: 'in',
    type: 'member',
    on: true,
    direction: 'asc',
  },
  {
    name: 'sort.age_asc',
    field: 'dob',
    type: 'age',
    on: true,
    direction: 'asc',
  },
  {
    name: 'sort.age_desc',
    field: 'dob',
    type: 'age',
    on: true,
    direction: 'desc',
  },
];

/**
 * Returns the translated value for the search field label.
 *
 * @return {string}
 */
const getSearchLabel = () => {
  return translate('global.search') as string;
};

/**
 * Returns the groups members filtered on first and last name by the components search string property.
 *
 * @return {Array<Member>}
 */
const filteredRecords = computed(() => {
  return groupMembers.value.filter((member: Member) => {
    if (!search.value) {
      return groupMembers;
    }
    return (
      member.firstname.toLowerCase().includes(search.value.toLowerCase()) ||
      member.lastname.toLowerCase().includes(search.value.toLowerCase())
    );
  });
});

const someMembersGrouped = computed(() => groupMembers.value.some(m => m.tags && m.tags.length));

/**
 * Returns the filtered records sorted in the selected order.
 * Default is alphabetically ascending by last name.
 *
 * @return {Array<Member>}
 */
const filteredAndSortedRecords = computed(() =>
  [...filteredRecords.value].sort((a: Member, b: Member) => {
    let comparison = 0;

    if (sortBy.value.field === 'in') {
      comparison = (a.in ? 1 : 0) - (b.in ? 1 : 0);
    } else if (sortBy.value.field === 'dob') {
      comparison = moment().diff(a.dob, 'years') - moment().diff(b.dob, 'years');
    } else if (typeof a[sortBy.value.field] === 'string' && typeof b[sortBy.value.field] === 'string') {
      comparison = a[sortBy.value.field].localeCompare(b[sortBy.value.field]);
    }

    // Apply direction
    comparison *= sortBy.value.direction === 'desc' ? -1 : 1;

    // Secondary sort if the values are equal -- lastname for dob, or firstname otherwise
    return (
      comparison ||
      (sortBy.value.field === 'dob' ? a.lastname.localeCompare(b.lastname) : a.firstname.localeCompare(b.firstname))
    );
  })
);

/**
 * Returns the filtered member records sorted in the selected order.
 * Default is alphabetically ascending by last name.
 *
 * param {Array<{name: string, field: string, type: string, on: boolean, direction: string}}
 * @return {void}
 */
function selectedSortOrder(selectedSort: {
  name: string;
  field: string;
  type: string;
  on: boolean;
  direction: string;
}): void {
  sortBy.value = selectedSort;
}

/**
 * Triggered by the @change event of the members attendance checkbox.
 * This is dependent on the members in or attended state either trigger marking them present or absent
 * via the appropriate store action.
 *
 * @param {Member} member
 */
async function checkRegister(member: Member): Promise<void> {
  if (!member.in || member?.sessions_remaining?.text !== 'P') {
    return updateRegister(member);
  }

  // Confirm popup for planned absence members
  const res = await confirm(translate('group_members.confirm_delete_planned_absence') as string, {
    title: translate('global.are_you_sure') as string,
    color: 'dark',
    icon: 'mdi-help-circle-outline',
    buttonTrueText: translate('global.ok') as string,
    buttonFalseText: translate('global.cancel') as string,
  });
  // on confirm copy update to object
  if (res) {
    return updateRegister(member);
  }
  // on cancel revert back to unchecked
  member.in = false;
}

function updateRegister(member: Member): void {
  store.dispatch('groupMembers/markMemberRegister', { member, group: group.value, date: groupDate.value });
}

/**
 * Returns a translation of the param string, in this case the name of a sort option.
 *
 * @return {string}
 */
function getTranslatedSortOptionName(key: string): string {
  return translate(key) as string;
}

function markAllMembers() {
  const mark = !allMembersMarked.value;
  filteredAndSortedRecords.value.forEach((member: Member) => {
    // Skip marking Planned Absences as present
    if (member?.sessions_remaining?.text === 'P') {
      return true;
    }
    member.in = mark;
    store.dispatch('groupMembers/markMemberRegister', { member, group: group.value, date: groupDate.value });
  });
}

function ungroupAllMembers() {
  filteredAndSortedRecords.value.forEach((member: Member) => {
    if (member.tags && member.tags.length) {
      const tags = [...member.tags];
      tags.forEach(tag => {
        store.dispatch('groupMembers/unassignMemberTag', { member, tag, groupId: groupId.value });
      });
    }
  });
}

const allMembersMarked = computed(() => {
  let allMarked = true;
  for (let index = 0; index < filteredAndSortedRecords.value.length; index++) {
    const member = filteredAndSortedRecords.value[index] as Member;
    // Ignore those members marked as a planned absence
    if (!member.in && member?.sessions_remaining?.text !== 'P') {
      allMarked = false;
      break;
    }
  }
  return allMarked;
});

function openMemberTagsDialog(member: Member) {
  memberTagsDialog.value?.openDialog(member);
}

function toggleShowNote(member: Member) {
  set(showNote.value, member.uuid, member.uuid in showNote.value ? !showNote.value[member.uuid] : true);
}

function noteVisible(member: Member) {
  return member.uuid in showNote.value ? showNote.value[member.uuid] : false;
}

return { __sfc: true,translate, confirm, store, route, groupId, groupLink, groupDate, groupName, groupTotal, group, groupMembers, enableAbilityGroups, memberTagsDialog, showNote, search, sortBy, sortOptions, getSearchLabel, filteredRecords, someMembersGrouped, filteredAndSortedRecords, selectedSortOrder, checkRegister, updateRegister, getTranslatedSortOptionName, markAllMembers, ungroupAllMembers, allMembersMarked, openMemberTagsDialog, toggleShowNote, noteVisible, RegisterMember, MemberTagsDialog, GroupedMemberView, MemberNote }
}

})